// FR-P3b: the "Generate quality levels" confirmation, and the status notice both on-demand media
// jobs (ladder and remux) report through once requested. The dialog says what the server will
// actually do (workers/src/transcode.rs `ladder`, rung choice from ffmpeg.rs `select_rungs`) —
// `POST /grackles/{id}/transcode` takes no body, so there are no options to offer and the dialog
// says so rather than inventing any.
import { Button, Callout } from "@grackle/ui";
import ConfirmDialog from "../components/ConfirmDialog";
import { ladderRungsFor, type MediaJobKind, type MediaJobPhase } from "./mediaJob";

function listOf(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export function QualityLevelsDialog({
  open,
  sourceHeight,
  onClose,
  onConfirm,
}: {
  open: boolean;
  sourceHeight: number | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const rungs = ladderRungsFor(sourceHeight);
  return (
    <ConfirmDialog
      open={open}
      title="Generate quality levels?"
      confirmLabel="Generate"
      onClose={onClose}
      onConfirm={onConfirm}
      message={
        <div className="quality-dialog">
          <p>
            Right now this recording streams at one quality, its original. Grackle will encode extra copies at lower
            resolutions
            {rungs ? (
              <>
                {" "}
                — <strong>{listOf(rungs)}</strong> —
              </>
            ) : null}{" "}
            so the player can drop to a smaller one automatically when a viewer's connection is slow, instead of
            stalling. Viewers also get a quality menu in the player.
          </p>
          <ul>
            <li>The recording stays watchable the whole time; nothing about the original changes.</li>
            <li>
              It runs on Grackle's servers and usually takes a few minutes — longer for long or high-resolution
              recordings. You can leave this page.
            </li>
            <li>It uses your organization's media-processing time, which is why it needs processing permission.</li>
          </ul>
          <p className="quality-dialog__note">
            There are no options to choose: levels are picked from the recording's own resolution, and Grackle never
            upscales.
          </p>
        </div>
      }
    />
  );
}

const LABELS: Record<MediaJobKind, { what: string; done: string }> = {
  transcode: {
    what: "Generating quality levels",
    done: "Quality levels are ready. Pick one from the player's quality menu, or leave it on Auto.",
  },
  remux: {
    what: "Preparing this recording for streaming",
    done: "This recording is ready for streaming.",
  },
};

/** The visible answer to "did anything happen?" after a media job is requested. */
export function MediaJobNotice({
  kind,
  phase,
  onDismiss,
  onRetry,
}: {
  kind: MediaJobKind | null;
  phase: MediaJobPhase;
  onDismiss: () => void;
  onRetry: () => void;
}) {
  if (!kind || phase.state === "idle") return null;
  const { what, done } = LABELS[kind];
  const dismiss = (
    <Button variant="ghost" size="sm" onClick={onDismiss}>
      Dismiss
    </Button>
  );

  switch (phase.state) {
    case "starting":
      return (
        <Callout tone="info" icon="circle-notch" aria-live="polite">
          {what}: sending the request…
        </Callout>
      );
    case "queued":
      return (
        <Callout tone="info" icon="clock" aria-live="polite">
          {what}: queued, waiting for a free worker. You can keep watching — this page updates by itself.
        </Callout>
      );
    case "running":
      return (
        <Callout tone="info" icon="circle-notch" aria-live="polite">
          {what}: encoding now. This usually takes a few minutes; you can keep watching or leave this page.
        </Callout>
      );
    case "retrying":
      return (
        <Callout tone="info" icon="arrow-clockwise" aria-live="polite">
          {what}: attempt {phase.attempt} didn't finish{phase.error ? ` (${phase.error})` : ""}. Grackle will retry
          automatically.
        </Callout>
      );
    case "done":
      return (
        <Callout tone="success" icon="check-circle" aria-live="polite">
          <div className="media-job-notice">
            <span>{done}</span>
            {dismiss}
          </div>
        </Callout>
      );
    case "failed":
      return (
        <Callout tone="danger" icon="warning-circle" aria-live="assertive">
          <div className="media-job-notice">
            <span>
              {what} failed: {phase.error}
            </span>
            <span className="media-job-notice__actions">
              <Button variant="secondary" size="sm" onClick={onRetry}>
                Try again
              </Button>
              {dismiss}
            </span>
          </div>
        </Callout>
      );
    case "slow":
      return (
        <Callout tone="info" icon="hourglass" aria-live="polite">
          <div className="media-job-notice">
            <span>{what} is taking longer than usual. It keeps running — reload this page later to see it.</span>
            {dismiss}
          </div>
        </Callout>
      );
  }
}
